import React, {useState, useEffect} from "react";
import {
    SwipeableDrawer, Box, Typography, ToggleButtonGroup, ToggleButton, Chip, Divider, Autocomplete, TextField, Switch,
    FormControlLabel, Button, Stack,
} from "@mui/material";
import {SeverityDot} from "./cells";
import {fmtMoneyShort, statusMeta, shortProvider, ALARM_STATUSES, INFO_STATUSES, IDLE_STATUSES, COMPARE_BASES, PRESETS} from "./format";
import {TYPE, TABULAR, NEUTRAL, SEV} from "./tokens";
import {severityOf} from "./format";

const Section = ({label, children}) => (
    <Box sx={{px: 2, py: 1.25}}>
        <Typography component="h3" sx={{...TYPE.eyebrow, mb: 0.75}}>{label}</Typography>
        {children}
    </Box>
);

const Puller = () => <Box aria-hidden="true" sx={{width: 32, height: 4, borderRadius: 2, bgcolor: "grey.300", mx: "auto", mt: 1, mb: 0.5}}/>;

/**
 * Phone bottom sheet with every filter. Apply-on-close: local draft, one commit.
 * `value` = {preset, statuses, groups, providers, groupBy, includeInactive, compareBase}; `statusCounts`; `groupOptions` [{prefix,label,rev}];
 * `providerOptions`; `countFor(draft)` → number of tags the draft would show.
 */
const FilterSheet = ({open, onClose, value, onApply, statusCounts, groupOptions, providerOptions, countFor}) => {
    const [draft, setDraft] = useState(value);
    useEffect(() => { if (open) setDraft(value); }, [open, value]);
    const set = (patch) => setDraft((d) => ({...d, ...patch}));
    const toggle = (key, v) => set({[key]: draft[key].includes(v) ? draft[key].filter((x) => x !== v) : [...draft[key], v]});
    const earning = groupOptions.filter((g) => g.rev > 0);
    const zero = groupOptions.filter((g) => !(g.rev > 0));
    const count = countFor ? countFor(draft) : null;
    const statuses = [...ALARM_STATUSES, ...INFO_STATUSES, ...IDLE_STATUSES, "OK"].filter((s) => statusCounts[s]);

    const commit = () => { onApply(draft); onClose(); };
    const reset = () => setDraft({preset: 7, statuses: [], groups: [], providers: [], groupBy: "status", includeInactive: false, compareBase: "base"});

    return (
        <SwipeableDrawer anchor="bottom" open={open} onClose={commit} onOpen={() => {}} disableSwipeToOpen disableDiscovery
                         PaperProps={{sx: {maxHeight: "85dvh", borderTopLeftRadius: 16, borderTopRightRadius: 16, display: "flex", flexDirection: "column"}}}>
            <Puller/>
            <Typography component="h2" sx={{fontSize: 16, fontWeight: 600, px: 2, pb: 0.5}}>Filters</Typography>
            <Box sx={{overflowY: "auto", flex: "1 1 auto"}}>
                <Section label="Range">
                    <ToggleButtonGroup exclusive fullWidth size="small" value={draft.preset} onChange={(e, v) => v && set({preset: v})} aria-label="Range">
                        {PRESETS.map((p) => <ToggleButton key={p.key} value={p.key} sx={{textTransform: "none", fontWeight: 600, py: 0.5}}>{p.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                </Section>
                <Divider sx={{borderColor: NEUTRAL.hairline}}/>
                <Section label="Status">
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {statuses.map((s) => {
                            const active = draft.statuses.includes(s);
                            const sev = severityOf(s);
                            return (
                                <Chip key={s} size="small" clickable onClick={() => toggle("statuses", s)} aria-pressed={active}
                                      icon={sev === "error" || sev === "warning" ? <SeverityDot status={s} size={7} sx={{ml: "8px !important"}}/> : undefined}
                                      label={`${statusMeta(s).label} · ${statusCounts[s]}`}
                                      sx={{
                                          height: 28, fontSize: 12, fontWeight: 600, ...TABULAR,
                                          bgcolor: active ? (t) => (sev === "error" || sev === "warning" ? SEV[sev].chipBg(t) : "rgba(25,118,210,.10)") : "transparent",
                                          border: "1px solid", borderColor: active ? (t) => (sev === "error" || sev === "warning" ? SEV[sev].dot(t) : t.palette.primary.main) : NEUTRAL.divider,
                                          color: active ? (t) => (sev === "error" || sev === "warning" ? SEV[sev].chipText(t) : t.palette.primary.main) : "text.primary",
                                      }}/>
                            );
                        })}
                    </Box>
                </Section>
                <Divider sx={{borderColor: NEUTRAL.hairline}}/>
                <Section label="Channel">
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                        {earning.map((g) => {
                            const active = draft.groups.includes(g.prefix);
                            return <Chip key={g.prefix} size="small" clickable onClick={() => toggle("groups", g.prefix)} aria-pressed={active}
                                         label={`${g.label} · ${fmtMoneyShort(g.rev)}`} color={active ? "primary" : "default"} variant={active ? "filled" : "outlined"}
                                         sx={{height: 28, fontSize: 12, fontWeight: 600, ...TABULAR}}/>;
                        })}
                    </Box>
                    {zero.length > 0 && (
                        <>
                            <Divider sx={{my: 1, borderColor: NEUTRAL.hairline}}/>
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.75}}>
                                {zero.map((g) => {
                                    const active = draft.groups.includes(g.prefix);
                                    return <Chip key={g.prefix} size="small" clickable onClick={() => toggle("groups", g.prefix)} aria-pressed={active}
                                                 label={`${g.label} · $0`} color={active ? "primary" : "default"} variant={active ? "filled" : "outlined"}
                                                 sx={{height: 28, fontSize: 12, color: active ? undefined : NEUTRAL.idleText, ...TABULAR}}/>;
                                })}
                            </Box>
                        </>
                    )}
                </Section>
                <Divider sx={{borderColor: NEUTRAL.hairline}}/>
                <Section label="Partner">
                    <Autocomplete multiple size="small" options={providerOptions} value={draft.providers} getOptionLabel={(v) => shortProvider(v)}
                                  onChange={(e, v) => set({providers: v})} limitTags={2}
                                  renderInput={(p) => <TextField {...p} placeholder={draft.providers.length ? "" : "All partners"}/>}/>
                </Section>
                <Divider sx={{borderColor: NEUTRAL.hairline}}/>
                <Section label="Group rows by">
                    <ToggleButtonGroup exclusive fullWidth size="small" value={draft.groupBy} onChange={(e, v) => v && set({groupBy: v})} aria-label="Group rows by">
                        {[["status", "Status"], ["group", "Channel"], ["none", "None"]].map(([v, l]) => (
                            <ToggleButton key={v} value={v} sx={{textTransform: "none", fontWeight: 600, py: 0.5}}>{l}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Section>
                <Divider sx={{borderColor: NEUTRAL.hairline}}/>
                <Section label="Yesterday compared with">
                    <ToggleButtonGroup exclusive fullWidth size="small" value={draft.compareBase || "base"} onChange={(e, v) => v && set({compareBase: v})} aria-label="Yesterday compared with">
                        {COMPARE_BASES.map((b) => (
                            <ToggleButton key={b.key} value={b.key} sx={{textTransform: "none", fontWeight: 600, py: 0.5}}>{b.label}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Typography sx={{...TYPE.caption, mt: 0.75}}>Drives every Δ yday on the page (sort chip, rows, detail). Δ prev (the week) is unchanged.</Typography>
                </Section>
                <Divider sx={{borderColor: NEUTRAL.hairline}}/>
                <Box sx={{px: 2, py: 0.5}}>
                    <FormControlLabel control={<Switch size="small" checked={Boolean(draft.includeInactive)} onChange={(e) => set({includeInactive: e.target.checked})}/>}
                                      label="Include silent tags" sx={{"& .MuiFormControlLabel-label": {fontSize: 13}}}/>
                    <Typography sx={{...TYPE.caption, ml: 4.5, mt: -0.5}}>tags with nothing sent and $0 in the range</Typography>
                </Box>
            </Box>
            <Stack direction="row" spacing={1} sx={{p: 1.5, pb: 1.5, borderTop: `1px solid ${NEUTRAL.divider}`, bgcolor: "background.paper", flex: "0 0 auto"}}>
                <Button variant="outlined" onClick={reset} sx={{textTransform: "none", flex: "0 0 auto"}}>Reset</Button>
                <Button variant="contained" disableElevation fullWidth onClick={commit} sx={{textTransform: "none", ...TABULAR}}>
                    Apply{count !== null ? ` · ${count} tags` : ""}
                </Button>
            </Stack>
        </SwipeableDrawer>
    );
};

export default FilterSheet;
